// Thin client for season-aware operations
// Uses the Cloud Function endpoint to upsert seasons and provides simple Firestore helpers.
import { doc, getDoc, getDocs, orderBy, query, collection } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const endpoint = process.env.REACT_APP_SAVE_SEASON;

export async function saveSeason(payload) {
  if (!endpoint) {
    throw new Error('REACT_APP_SAVE_SEASON is not set');
  }
  const { auth } = await import('../firebaseConfig');
  const currentUser = auth?.currentUser;
  const token = currentUser ? await currentUser.getIdToken() : null;
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let message = `saveSeason failed (${res.status})`;
    try {
      const data = await res.json();
      if (data?.error) message = data.error;
    } catch (_) {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json();
}

export async function fetchActiveSeason(teamId) {
  if (!teamId) return { team: null, season: null, year: null };
  const teamRef = doc(db, 'teams', teamId);
  const teamSnap = await getDoc(teamRef);
  if (!teamSnap.exists()) return { team: null, season: null, year: null };

  const team = teamSnap.data();
  const year =
    team.currentSeasonYear ||
    team.latestSeasonId ||
    new Date().getFullYear().toString();

  const seasonRef = doc(db, `teams/${teamId}/seasons/${year}`);
  const seasonSnap = await getDoc(seasonRef);
  const season = seasonSnap.exists() ? seasonSnap.data() : null;

  return { team, season, year: String(year) };
}

export async function fetchActiveSeasonDoc(teamId) {
  if (!teamId) return { team: null, season: null, seasonId: null, year: null };

  const teamRef = doc(db, 'teams', teamId);
  const teamSnap = await getDoc(teamRef);
  if (!teamSnap.exists()) return { team: null, season: null, seasonId: null, year: null };

  const team = teamSnap.data();
  let seasons = [];
  try {
    seasons = await listSeasons(teamId);
  } catch (err) {
    console.error('Failed to list seasons', err);
  }

  const normalized = seasons.map((s) => ({
    ...s,
    id: String(s.id),
    year: s?.year != null ? String(s.year) : null,
    status: s?.status || '',
  }));

  const targetYear = team.currentSeasonYear ? String(team.currentSeasonYear) : null;
  const byYear = targetYear
    ? normalized.find((s) => s.year === targetYear || s.id === targetYear)
    : null;
  const byActive = normalized.find((s) => (s.status || '').toLowerCase() === 'active');
  const chosen = byYear || byActive || normalized[0] || null;

  let seasonId = chosen ? String(chosen.id) : null;
  let year = chosen?.year || targetYear || null;
  let season = chosen || null;

  if (!season && seasonId) {
    const seasonSnap = await getDoc(doc(db, `teams/${teamId}/seasons/${seasonId}`));
    season = seasonSnap.exists() ? seasonSnap.data() : null;
    if (!year && season?.year != null) year = String(season.year);
  }

  if (!seasonId) {
    const fallbackId = team.currentSeasonId || team.latestSeasonId || team.currentSeasonYear;
    if (fallbackId) seasonId = String(fallbackId);
  }

  return { team, season, seasonId, year };
}

export async function listSeasons(teamId) {
  if (!teamId) return [];
  const seasonsRef = collection(db, `teams/${teamId}/seasons`);
  const q = query(seasonsRef, orderBy('year', 'desc'));
  const snaps = await getDocs(q);
  return snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
}
